<div class="flex flex-col gap-1.5">
  <!-- Single row: search flexes, everything else is fixed-width and collapses to icons. -->
  <div class="flex items-center gap-1.5">
    <label
      class="flex min-w-0 flex-1 items-center rounded-lg border border-neutral-200 bg-neutral-0 pr-1 pl-2 transition-colors duration-150 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/15 motion-reduce:transition-none"
    >
      <mat-icon
        class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
        [matTooltip]="'guaranteeV2.message.searchPlaceholder' | translate"
        >search</mat-icon
      >
      <input
        class="min-w-0 flex-1 bg-transparent px-2 py-1.5 text-base text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
        type="text"
        [formControl]="searchControl"
        [attr.aria-label]="'guaranteeV2.label.search' | translate"
        [attr.placeholder]="'guaranteeV2.message.searchPlaceholder' | translate"
        (keydown.escape)="clearSearch()"
      />
      @if (searchValue()) {
        <button
          class="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none sm:size-6"
          type="button"
          [attr.aria-label]="'guaranteeV2.action.clearSearch' | translate"
          [matTooltip]="'guaranteeV2.action.clearSearch' | translate"
          (click)="clearSearch()"
        >
          <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
        </button>
      }
    </label>

    <!-- Filters: one button, badge shows how many facets are on. -->
    <button
      guaranteesToggle
      size="lg"
      shape="card"
      menuTrigger
      type="button"
      [pressed]="activeFacetCount() > 0"
      [disabled]="busy()"
      [matMenuTriggerFor]="filterMenu"
      [matTooltip]="'guaranteeV2.message.filtersHint' | translate"
    >
      <mat-icon class="h-4! w-4! text-base! leading-4!">tune</mat-icon>
      <span class="hidden md:inline">{{ "guaranteeV2.action.filters" | translate }}</span>
      @if (activeFacetCount() > 0) {
        <span class="inline-flex size-4 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-neutral-0 tabular-nums">{{ activeFacetCount() }}</span>
      }
    </button>

    <!-- Date range: label on wide screens, icon only when space is tight. -->
    <button
      guaranteesToggle
      size="lg"
      shape="card"
      menuTrigger
      type="button"
      [disabled]="busy()"
      [matMenuTriggerFor]="rangeMenu"
      [matTooltip]="'guaranteeV2.message.dateRangeHint' | translate"
    >
      <mat-icon class="h-4! w-4! text-base! leading-4!">date_range</mat-icon>
      <span class="hidden sm:inline">{{ activeRangeLabel() | translate }}</span>
    </button>

    <!--
      How the loaded window is read. Sits beside the range because the two are the same kind of
      control — one chooses what is loaded, the other what is made of it — and a mode never
      refetches or disturbs the facets.
    -->
    <button
      guaranteesToggle
      size="lg"
      shape="card"
      menuTrigger
      type="button"
      [pressed]="viewMode() !== 'timeline'"
      [disabled]="busy()"
      [matMenuTriggerFor]="viewModeMenu"
      [matTooltip]="'guaranteeV2.label.viewMode' | translate"
    >
      <mat-icon class="h-4! w-4! text-base! leading-4!">{{ activeViewModeIcon() }}</mat-icon>
      <span class="hidden sm:inline">{{ activeViewModeLabel() | translate }}</span>
    </button>

    <!--
      Panels stay reachable while loading: cases belong to the member rather than to the window, and
      the findings panel is how a stalled load is investigated. Only the breach count waits.
    -->
    <button
      guaranteesToggle
      size="lg"
      shape="card"
      type="button"
      [pressed]="sidePanel() === 'cases'"
      [attr.aria-label]="'guaranteeV2.label.cases' | translate"
      [matTooltip]="'guaranteeV2.message.casesHint' | translate"
      (click)="panelToggled.emit('cases')"
    >
      <mat-icon class="h-4! w-4! text-base! leading-4!">folder_managed</mat-icon>
      <!-- Zero is stated rather than left blank: no case is a thing to answer, not the absence of news. -->
      <span class="tabular-nums">{{ caseCount() }}</span>
    </button>

    <button
      guaranteesToggle
      size="lg"
      shape="card"
      type="button"
      [pressed]="sidePanel() === 'breaches'"
      [tone]="!busy() && breachCount() > 0 ? 'warning' : 'neutral'"
      [attr.aria-label]="'guaranteeV2.label.breaches' | translate"
      [matTooltip]="'guaranteeV2.message.breachesHint' | translate"
      (click)="panelToggled.emit('breaches')"
    >
      <mat-icon class="h-4! w-4! text-base! leading-4!">rule</mat-icon>
      @if (!busy() && breachCount() > 0) {
        <span class="tabular-nums">{{ breachCount() }}</span>
      }
    </button>

    <!-- Last: the controls before it narrow what is on screen, this one adds to it. -->
    @if (canCreate()) {
      <button
        type="button"
        [class]="buttons.primary.md + ' shrink-0'"
        [matTooltip]="'guaranteeV2.message.createGuaranteeHint' | translate"
        (click)="createGuarantee()"
      >
        <mat-icon class="h-4! w-4! text-base! leading-4!">note_add</mat-icon>
        <span class="hidden xl:inline">{{ "guaranteeV2.action.createGuarantee" | translate }}</span>
      </button>
    }
  </div>

  <!--
    Second row appears only once something is filtered, so the header is one line at rest. It reports
    the *applied* term rather than the typed one — this row says what is narrowing the stream, and
    until the debounce settles the two are not the same thing.
  -->
  @if (activeFilters().length > 0 || appliedSearch()) {
    <div class="flex flex-wrap items-center gap-1.5">
      @if (appliedSearch(); as appliedSearch) {
        <button
          type="button"
          [class]="buttons.neutral.sm"
          [attr.aria-label]="'guaranteeV2.action.clearSearch' | translate"
          [matTooltip]="'guaranteeV2.action.clearSearch' | translate"
          (click)="clearSearch()"
        >
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5! text-neutral-400">search</mat-icon>
          <span class="max-w-40 truncate">{{ appliedSearch }}</span>
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">close</mat-icon>
        </button>
      }

      @for (filter of activeFilters(); track filter.facet + filter.value) {
        <button
          type="button"
          [class]="buttons.soft.sm"
          [matTooltip]="'guaranteeV2.message.removeFilterHint' | translate"
          (click)="toggleFacet(filter.facet, filter.value)"
        >
          @if (filter.labelKey) {
            {{ filter.labelKey | translate }}
          } @else {
            <!-- A diagnosis branch names itself; there is no key to translate a code by. -->
            <span class="font-mono">{{ filter.value }}</span>
          }
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">close</mat-icon>
        </button>
      }

      <button
        type="button"
        [class]="buttons.ghost.sm"
        [matTooltip]="'guaranteeV2.message.clearFiltersHint' | translate"
        (click)="clearAll()"
      >
        {{ "guaranteeV2.action.clearFilters" | translate }}
      </button>

      <span class="ml-auto text-sm text-neutral-400 tabular-nums lg:hidden">
        {{ "guaranteeV2.label.visitsCount" | translate: { count: visitCount() } }}
      </span>
    </div>
  }
</div>

<mat-menu
  #filterMenu="matMenu"
  [class]="menu.panel"
>
  <ng-template matMenuContent>
    <!--
      Kept open on click so several facets can be toggled in one pass. Escape is deliberately left
      to bubble: the panel's own handler is what closes the menu on it.
    -->
    <div
      class="flex max-h-[70vh] w-80 flex-col gap-3 overflow-x-hidden overflow-y-auto p-2"
      (click)="$event.stopPropagation()"
    >
      <!--
        Every group is a fold whose header reports what is picked inside it, which is what makes
        folding safe: nothing set can hide behind a closed one.
      -->
      @for (group of filterGroups(); track group.id) {
        <section
          class="flex flex-col gap-1"
          [class]="$first ? '' : 'border-t border-neutral-100 pt-2'"
        >
          <button
            class="flex items-center gap-1 rounded-lg px-1.5 py-1 text-left hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
            type="button"
            [attr.aria-expanded]="group.open"
            [matTooltip]="group.hint ? (group.hint | translate) : ''"
            [matTooltipDisabled]="!group.hint"
            (click)="toggleGroup(group.id)"
          >
            <span class="min-w-0 flex-1 text-sm font-semibold text-neutral-400">{{ group.labelKey | translate }}</span>
            @if (group.count) {
              <span class="inline-flex size-4 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-neutral-0 tabular-nums">{{ group.count }}</span>
            }
            <mat-icon class="h-4! w-4! text-base! leading-4! text-neutral-300">{{ group.open ? "expand_less" : "expand_more" }}</mat-icon>
          </button>

          @if (group.open) {
            @if (group.isTree) {
              <guarantees-icd10-filter
                [facets]="icd10Facets()"
                (prefixToggled)="icd10PrefixToggled.emit($event)"
                (cursorChanged)="icd10CursorChanged.emit($event)"
                (cleared)="icd10Cleared.emit()"
              ></guarantees-icd10-filter>
            } @else {
              @for (row of group.rows; track row.value) {
                <button
                  [class]="checkRow"
                  type="button"
                  [attr.aria-pressed]="row.active"
                  (click)="toggleFacet(group.id, row.value)"
                >
                  <guarantees-check-box [checked]="row.active"></guarantees-check-box>
                  <span class="min-w-0 flex-1 font-medium text-neutral-700">{{ row.labelKey | translate }}</span>
                  <span class="text-neutral-400 tabular-nums">{{ row.count }}</span>
                </button>
              }
            }
          }
        </section>
      }

      <!-- Last, and set apart: every group above narrows what is on screen, this one adds to it. -->
      <section class="flex flex-col gap-1 border-t border-neutral-100 pt-2">
        <button
          [class]="checkRow"
          type="button"
          [attr.aria-pressed]="showDeleted()"
          [matTooltip]="'guaranteeV2.message.showDeletedHint' | translate"
          (click)="showDeletedToggled.emit()"
        >
          <guarantees-check-box [checked]="showDeleted()"></guarantees-check-box>
          <span class="min-w-0 flex-1 font-medium text-neutral-700">{{ "guaranteeV2.action.showDeleted" | translate }}</span>
        </button>
      </section>

      @if (activeFacetCount() > 0) {
        <button
          class="flex items-center justify-center gap-1 rounded-lg border border-neutral-200 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="clearAll()"
        >
          <mat-icon class="h-4! w-4! text-base! leading-4!">filter_alt_off</mat-icon>
          {{ "guaranteeV2.action.clearFilters" | translate }}
        </button>
      }
    </div>
  </ng-template>
</mat-menu>

<mat-menu
  #viewModeMenu="matMenu"
  [class]="menu.panel"
>
  <!-- Lazy: without it the panel's whole subtree is built and re-evaluated while the menu is shut. -->
  <ng-template matMenuContent>
    <span [class]="menu.heading">{{ "guaranteeV2.label.viewMode" | translate }}</span>
    @for (mode of viewModeRows(); track mode.mode) {
      <button
        mat-menu-item
        [class]="menu.item"
        type="button"
        [attr.aria-current]="mode.active ? 'true' : null"
        [matTooltip]="mode.hintKey | translate"
        (click)="viewModeChanged.emit(mode.mode)"
      >
        <mat-icon [class]="menu.icon + ' ' + (mode.active ? 'text-primary-600' : 'text-neutral-300')">{{ mode.icon }}</mat-icon>
        <span [class]="menu.label + ' ' + (mode.active ? 'text-primary-700' : '')">{{ mode.labelKey | translate }}</span>
        @if (mode.active) {
          <mat-icon class="ml-2! h-3.5! w-3.5! text-sm! leading-3.5! text-primary-600">check</mat-icon>
        }
        @if (mode.count > 0) {
          <span class="ml-auto pl-2 text-sm font-semibold text-neutral-400 tabular-nums">{{ mode.count }}</span>
        }
      </button>
    }
  </ng-template>
</mat-menu>

<mat-menu
  #rangeMenu="matMenu"
  [class]="menu.panel"
>
  <ng-template matMenuContent>
    <span [class]="menu.heading">{{ "guaranteeV2.message.dateRangeHint" | translate }}</span>
    @for (preset of rangeRows(); track preset.preset) {
      <button
        mat-menu-item
        [class]="menu.item"
        type="button"
        [attr.aria-current]="preset.active ? 'true' : null"
        [matTooltip]="'guaranteeV2.message.rangePresetHint' | translate"
        (click)="rangePresetChanged.emit(preset.preset)"
      >
        <mat-icon [class]="menu.icon + ' ' + (preset.active ? 'text-primary-600' : 'text-neutral-300')">{{
          preset.active ? "radio_button_checked" : "radio_button_unchecked"
        }}</mat-icon>
        <span [class]="menu.label + ' ' + (preset.active ? 'text-primary-700' : '')">{{ preset.labelKey | translate }}</span>
      </button>
    }
  </ng-template>
</mat-menu>
